import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { SPECIES } from '@zt/content';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Textarea,
  buttonStyles,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CustomerTag, RfmSummary, SegmentMembers, SegmentPreview, SegmentRow } from '../lib/types';

/**
 * Pazarlama → Segmentler (F6-02, ADR-083): RFM grupları (gece hesaplanır) ve kurallı müşteri segmentleri.
 * Segment üyeleri sunucuda her istekte kurallardan hesaplanır; listedeki sayı son sayımdır.
 */

const RFM_KEYS = [
  'champions',
  'loyal',
  'new',
  'potential',
  'need_attention',
  'cant_lose',
  'at_risk',
  'hibernating',
  'lost',
] as const;
type SegField =
  | 'order_count'
  | 'total_spent'
  | 'last_order_days'
  | 'registered_days'
  | 'species'
  | 'tag'
  | 'city'
  | 'kind'
  | 'consent'
  | 'consent_source'
  | 'rfm';
const FIELDS: SegField[] = [
  'rfm',
  'order_count',
  'total_spent',
  'last_order_days',
  'registered_days',
  'tag',
  'species',
  'city',
  'kind',
  'consent',
  'consent_source',
];
const NUMERIC: SegField[] = ['order_count', 'total_spent', 'last_order_days', 'registered_days'];
type Op = 'is' | 'is_not' | 'gte' | 'lte';
/** Panelde tutar kullanıcının yazdığı biçimde tutulur; gönderirken kuruşa çevrilir. */
type Condition = { field: SegField; op: Op; value: string };

const useRfm = () => useQuery({ queryKey: ['rfm'], queryFn: () => api.get<RfmSummary>('/v1/admin/crm/rfm') });

/** Eşik değerleri metinlere (gün/sipariş sayıları) aktarılır; kodda sabit sayı yazılmaz. */
function thresholdVars(s: RfmSummary | undefined): Record<string, number | string> {
  const r: readonly number[] = s?.thresholds.recencyDays ?? [];
  const f: readonly number[] = s?.thresholds.frequencyMinOrders ?? [];
  const v = (n: number | undefined) => n ?? '—';
  return {
    r5: v(r[0]),
    r4: v(r[1]),
    r3: v(r[2]),
    r2: v(r[3]),
    f2: v(f[0]),
    f3: v(f[1]),
    f4: v(f[2]),
    f5: v(f[3]),
  };
}

export function SegmentsPage() {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const money = useMoney();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const manage = can('marketing.segment.manage');
  const rfm = useRfm();
  const list = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<SegmentRow[]>('/v1/admin/segments'),
  });
  const recompute = useApiMutation(() => api.post<RfmSummary>('/v1/admin/crm/rfm/recompute', {}), {
    invalidate: [['rfm'], ['segments']],
    success: t('admin.segments.recomputed'),
  });
  const saveGroup = useApiMutation(
    (group: string) =>
      api.post<SegmentRow>('/v1/admin/segments', {
        name: t(`admin.segments.groups.${group}`),
        description: t(`admin.segments.groupHints.${group}`, thresholdVars(rfm.data)),
        rules: { match: 'all', conditions: [{ field: 'rfm', op: 'is', value: group }] },
      }),
    {
      invalidate: [['segments']],
      success: t('admin.segments.savedAsSegment'),
      onSuccess: (r) => void navigate({ to: '/segmentler/$id', params: { id: r.id } }),
    },
  );
  const s = rfm.data;
  const vars = thresholdVars(s);
  const total = s ? s.segments.reduce((a, g) => a + g.customers, 0) + s.noOrders : 0;
  const pct = (n: number) => (total ? Math.round((n * 100) / total) : 0);
  const rows = s
    ? [
        ...s.segments.map((g) => ({ key: g.key as string, customers: g.customers, spent: g.spentMinor })),
        { key: 'none', customers: s.noOrders, spent: null as number | null },
      ]
    : [];
  return (
    <PageFrame
      helpKey="page.segments"
      actions={
        manage && (
          <Link to="/segmentler/$id" params={{ id: 'yeni' }} className={buttonStyles({ variant: 'primary' })}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.segments.add')}
          </Link>
        )
      }
    >
      <Section
        title={t('admin.segments.rfmTitle')}
        description={t('admin.segments.rfmLead')}
        action={<HelpTip k="segments.rfm" />}
      >
        {!s ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
              <span className={s.computedAt ? 'text-ink-2' : 'text-ink-3'}>
                {s.computedAt
                  ? t('admin.segments.computedAt', { date: formatDate(s.computedAt, true) })
                  : t('admin.segments.neverComputed')}
              </span>
              {manage && (
                <span className="inline-flex items-center gap-1">
                  <Button size="sm" loading={recompute.isPending} onClick={() => recompute.mutate(undefined)}>
                    <RefreshCw className="h-4 w-4" aria-hidden />
                    {t('admin.segments.recompute')}
                  </Button>
                  <HelpTip k="segments.recompute" />
                </span>
              )}
            </div>
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.segments.rfmTitle')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.segments.colName')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.segments.colMembers')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.segments.colSpent')}</th>
                    {manage && (
                      <th className="px-3 py-2 text-end font-medium">
                        <span className="sr-only">{t('admin.segments.saveAsSegment')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((g) => (
                    <tr key={g.key} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <span className="font-medium">{t(`admin.segments.groups.${g.key}`)}</span>
                        <span className="block text-xs text-ink-3">
                          {t(`admin.segments.groupHints.${g.key}`, vars)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-end">
                        <span className="tabular-nums">{formatNumber(g.customers)}</span>
                        <span className="mt-1 block text-xs text-ink-3">
                          {t('admin.segments.groupShare', { pct: pct(g.customers) })}
                        </span>
                        <span
                          aria-hidden
                          className="mt-1 ms-auto block h-1 w-24 overflow-hidden rounded-full bg-surface-2"
                        >
                          <span className="block h-full bg-pine" style={{ width: `${pct(g.customers)}%` }} />
                        </span>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {g.spent === null || g.customers === 0 ? '—' : money(g.spent, s.currency)}
                      </td>
                      {manage && (
                        <td className="px-3 py-2 text-end">
                          <span className="inline-flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={t('admin.segments.saveAsSegmentLabel', {
                                group: t(`admin.segments.groups.${g.key}`),
                              })}
                              loading={saveGroup.isPending && saveGroup.variables === g.key}
                              onClick={() => saveGroup.mutate(g.key)}
                            >
                              {t('admin.segments.saveAsSegment')}
                            </Button>
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 flex items-start gap-1 text-xs text-ink-3">
              {t('admin.segments.thresholds', vars)} <HelpTip k="segments.save_rfm" />
            </p>
          </>
        )}
      </Section>

      <Section
        title={t('admin.segments.listTitle')}
        description={t('admin.segments.listLead')}
        action={<HelpTip k="segments.list" />}
      >
        {!list.data ? (
          <Spinner label={t('common.loading')} />
        ) : list.data.length === 0 ? (
          <EmptyState title={t('admin.segments.emptyTitle')} description={t('admin.segments.emptyLead')} />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.segments.listTitle')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.segments.colName')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.segments.colMembers')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.segments.colCounted')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((x) => (
                  <tr key={x.id} className="border-t border-line align-top">
                    <td className="px-3 py-2">
                      <Link
                        to="/segmentler/$id"
                        params={{ id: x.id }}
                        className="font-medium text-pine underline-offset-2 hover:underline"
                      >
                        {x.name}
                      </Link>
                      {x.description && <span className="block text-xs text-ink-3">{x.description}</span>}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(x.memberCount)}</td>
                    <td className="px-3 py-2 text-end text-ink-2">{formatDate(x.countedAt, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </PageFrame>
  );
}

export function SegmentEditorPage() {
  const t = useT();
  const { id } = useParams({ from: '/app/segmentler/$id' });
  const isNew = id === 'yeni';
  const q = useQuery({
    queryKey: ['segment', id],
    queryFn: () => api.get<SegmentRow>(`/v1/admin/segments/${id}`),
    enabled: !isNew,
  });
  const rfm = useRfm();
  if ((!isNew && !q.data) || !rfm.data) return <Spinner label={t('common.loading')} />;
  return (
    <SegmentEditor
      key={q.data?.version ?? 'new'}
      row={isNew ? null : (q.data ?? null)}
      currency={rfm.data.currency}
    />
  );
}

/** Sunucudaki kanonik koşulu düzenleme biçimine çevirir (tutar kuruştan "1.500,00" biçimine). */
const toForm = (c: { field: string; op: string; value: string }): Condition => ({
  field: c.field as SegField,
  op: c.op as Op,
  value: c.field === 'total_spent' ? minorToInput(Number(c.value)) : c.value,
});

function SegmentEditor({ row, currency }: { row: SegmentRow | null; currency: string }) {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const manage = can('marketing.segment.manage');
  const [f, setF] = useState({
    name: row?.name ?? '',
    description: row?.description ?? '',
    match: (row?.rules.match ?? 'all') as 'all' | 'any',
    conditions: (row?.rules.conditions ?? [{ field: 'rfm', op: 'is', value: '' }]).map(toForm),
  });
  const [preview, setPreview] = useState<SegmentPreview | null>(null);
  /** Tutar kuruşa çevrilir; okunamazsa yazıldığı gibi gider ve sunucu "geçersiz koşul" der. */
  const wire = () => ({
    match: f.match,
    conditions: f.conditions.map((c) => ({
      ...c,
      value: c.field === 'total_spent' ? String(toMinor(c.value, currency) ?? c.value) : c.value,
    })),
  });
  const body = () => ({
    name: f.name,
    description: f.description.trim() || null,
    rules: wire(),
  });
  const save = useApiMutation(
    () =>
      row
        ? api.patch<SegmentRow>(`/v1/admin/segments/${row.id}`, { ...body(), version: row.version })
        : api.post<SegmentRow>('/v1/admin/segments', body()),
    {
      invalidate: [['segments'], ['segment'], ['segment-members']],
      success: t('admin.segments.saved'),
      silentFieldErrors: true,
      onSuccess: (r) => {
        if (!row) void navigate({ to: '/segmentler/$id', params: { id: r.id } });
      },
    },
  );
  const runPreview = useApiMutation(
    () => api.post<SegmentPreview>('/v1/admin/segments/preview', { rules: wire() }),
    { silentFieldErrors: true, onSuccess: (r) => setPreview(r) },
  );
  const del = useApiMutation(() => api.del(`/v1/admin/segments/${row!.id}`), {
    invalidate: [['segments']],
    success: t('admin.segments.deleted'),
    onSuccess: () => void navigate({ to: '/segmentler' }),
  });
  const err = (k: string) => fieldError(save.error, k) ?? fieldError(runPreview.error, k);
  const setConditions = (match: 'all' | 'any', conditions: Condition[]) => {
    setF({ ...f, match, conditions });
    setPreview(null);
  };
  return (
    <PageFrame
      helpKey="page.segment_editor"
      title={row ? row.name : t('admin.segments.add')}
      actions={
        <>
          <Link to="/segmentler" className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.segments.backToList')}
          </Link>
          {manage && row && (
            <span className="inline-flex items-center gap-1">
              <ConfirmButton
                variant="ghost"
                message={t('admin.segments.deleteConfirm', { name: row.name })}
                onConfirm={() => del.mutateAsync(undefined)}
              >
                {t('common.delete')}
              </ConfirmButton>
              <HelpTip k="segments.delete" />
            </span>
          )}
          {manage && (
            <span className="inline-flex items-center gap-1">
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
                {t('common.save')}
              </Button>
              <HelpTip k="segments.save" />
            </span>
          )}
        </>
      }
    >
      <Section title={t('admin.segments.basics')}>
        <div className="grid gap-4">
          <Field
            label={t('admin.segments.name')}
            required
            error={err('name')}
            help={<HelpTip k="segments.name" />}
          >
            {(p) => (
              <Input
                {...p}
                value={f.name}
                maxLength={80}
                disabled={!manage}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.segments.description')}
            optionalLabel={t('common.optional')}
            error={err('description')}
            help={<HelpTip k="segments.description" />}
          >
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={f.description}
                maxLength={300}
                disabled={!manage}
                onChange={(e) => setF({ ...f, description: e.target.value })}
              />
            )}
          </Field>
        </div>
      </Section>

      <Section
        title={t('admin.segments.rulesTitle')}
        description={t('admin.segments.rulesLead')}
        action={<HelpTip k="segments.conditions" />}
      >
        <RuleBuilder
          match={f.match}
          conditions={f.conditions}
          currency={currency}
          disabled={!manage}
          onChange={setConditions}
          error={(i) => err(`rules.conditions.${i}`) ?? err(`rules.conditions.${i}.value`)}
          listError={err('rules.conditions')}
          onPreview={() => runPreview.mutate(undefined)}
          previewing={runPreview.isPending}
        />
      </Section>

      {preview && (
        <Section
          title={t('admin.segments.previewTitle', { count: preview.count })}
          description={t('admin.segments.previewLead')}
        >
          <MemberTable items={preview.sample} />
        </Section>
      )}

      {row && !preview && <Members id={row.id} count={row.memberCount} />}
    </PageFrame>
  );
}

function RuleBuilder({
  match,
  conditions,
  currency,
  disabled,
  onChange,
  error,
  listError,
  onPreview,
  previewing,
}: {
  match: 'all' | 'any';
  conditions: Condition[];
  currency: string;
  disabled: boolean;
  onChange: (match: 'all' | 'any', conditions: Condition[]) => void;
  error: (i: number) => string | undefined;
  listError?: string;
  onPreview: () => void;
  previewing: boolean;
}) {
  const t = useT();
  const tags = useQuery({
    queryKey: ['customer-tags'],
    queryFn: () => api.get<CustomerTag[]>('/v1/admin/customer-tags'),
  });
  const setAt = (i: number, c: Condition) =>
    onChange(
      match,
      conditions.map((x, j) => (j === i ? c : x)),
    );
  const choose = <option value="">{t('admin.segments.choose')}</option>;
  const valueInput = (c: Condition, i: number) => {
    const common = { 'aria-label': t('admin.segments.value'), disabled, value: c.value };
    const pick = (options: Array<{ v: string; label: string }>) => (
      <Select {...common} onChange={(e) => setAt(i, { ...c, value: e.target.value })}>
        {choose}
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.label}
          </option>
        ))}
      </Select>
    );
    switch (c.field) {
      case 'rfm':
        return pick([...RFM_KEYS, 'none'].map((k) => ({ v: k, label: t(`admin.segments.groups.${k}`) })));
      case 'species':
        return pick(SPECIES.map((s) => ({ v: s, label: t(`admin.products.speciesNames.${s}`) })));
      case 'kind':
        return pick(
          (['dealer', 'retail'] as const).map((k) => ({ v: k, label: t(`admin.segments.kinds.${k}`) })),
        );
      case 'consent':
        return pick(
          (['email', 'sms', 'call', 'whatsapp'] as const).map((k) => ({
            v: k,
            label: t(`admin.customers.channels.${k}`),
          })),
        );
      case 'consent_source':
        return pick(
          (['register', 'checkout', 'account', 'import', 'admin'] as const).map((k) => ({
            v: k,
            label: t(`admin.customers.consentSource.${k}`),
          })),
        );
      case 'tag':
        return (
          <>
            <Input
              {...common}
              list="segment-tags"
              maxLength={40}
              onChange={(e) => setAt(i, { ...c, value: e.target.value })}
            />
            <datalist id="segment-tags">
              {(tags.data ?? []).map((x) => (
                <option key={x.tag} value={x.tag} />
              ))}
            </datalist>
          </>
        );
      case 'total_spent':
        return (
          <Input
            {...common}
            aria-label={t('admin.segments.amountIn', { currency })}
            inputMode="decimal"
            maxLength={16}
            onChange={(e) => setAt(i, { ...c, value: e.target.value })}
          />
        );
      case 'order_count':
      case 'last_order_days':
      case 'registered_days':
        return (
          <Input
            {...common}
            aria-label={
              c.field === 'order_count'
                ? t('admin.segments.value')
                : `${t('admin.segments.value')} (${t('admin.segments.days')})`
            }
            inputMode="numeric"
            maxLength={5}
            onChange={(e) => setAt(i, { ...c, value: e.target.value.replace(/\D/g, '') })}
          />
        );
      default:
        return (
          <Input {...common} maxLength={60} onChange={(e) => setAt(i, { ...c, value: e.target.value })} />
        );
    }
  };
  return (
    <div className="space-y-3">
      <Field label={t('admin.segments.match')} help={<HelpTip k="segments.match" />}>
        {(p) => (
          <Select
            {...p}
            value={match}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value as 'all' | 'any', conditions)}
          >
            <option value="all">{t('admin.segments.matchAll')}</option>
            <option value="any">{t('admin.segments.matchAny')}</option>
          </Select>
        )}
      </Field>
      <ol className="space-y-2" aria-label={t('admin.segments.conditions')}>
        {conditions.map((c, i) => {
          const ops: Op[] = NUMERIC.includes(c.field) ? ['gte', 'lte'] : ['is', 'is_not'];
          return (
            <li key={i} className="rounded-md border border-line p-3">
              <div className="grid gap-2 sm:grid-cols-[14rem_9rem_1fr_auto]">
                <Select
                  aria-label={t('admin.segments.field')}
                  value={c.field}
                  disabled={disabled}
                  onChange={(e) => {
                    const field = e.target.value as SegField;
                    setAt(i, { field, op: NUMERIC.includes(field) ? 'gte' : 'is', value: '' });
                  }}
                >
                  {FIELDS.map((x) => (
                    <option key={x} value={x}>
                      {t(`admin.segments.fields.${x}`)}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label={t('admin.segments.op')}
                  value={c.op}
                  disabled={disabled}
                  onChange={(e) => setAt(i, { ...c, op: e.target.value as Op })}
                >
                  {ops.map((o) => (
                    <option key={o} value={o}>
                      {t(`admin.segments.ops.${o}`)}
                    </option>
                  ))}
                </Select>
                {valueInput(c, i)}
                {!disabled && (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t('admin.segments.removeCondition')}
                    onClick={() =>
                      onChange(
                        match,
                        conditions.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
              {error(i) && (
                <p role="alert" className="mt-1 text-sm text-rose">
                  {error(i)}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {listError && (
        <p role="alert" className="text-sm text-rose">
          {listError}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {!disabled && conditions.length < 10 && (
          <Button
            size="sm"
            onClick={() => onChange(match, [...conditions, { field: 'tag', op: 'is', value: '' }])}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.segments.addCondition')}
          </Button>
        )}
        <Button size="sm" loading={previewing} onClick={onPreview} disabled={!conditions.length}>
          {t('admin.segments.preview')}
        </Button>
        <HelpTip k="segments.preview" />
      </div>
    </div>
  );
}

function Members({ id, count }: { id: string; count: number }) {
  const t = useT();
  const [cursor, setCursor] = useState<string | null>(null);
  const [pages, setPages] = useState<SegmentMembers['items']>([]);
  const q = useQuery({
    queryKey: ['segment-members', id, cursor],
    queryFn: () =>
      api.get<SegmentMembers>(
        `/v1/admin/segments/${id}/members?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
      ),
  });
  const items = [...pages, ...(q.data?.items ?? [])];
  return (
    <Section title={t('admin.segments.membersTitle', { count })} action={<HelpTip k="segments.members" />}>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-3">{t('admin.segments.membersEmpty')}</p>
      ) : (
        <>
          <MemberTable items={items} />
          {q.data.nextCursor && (
            <Button
              className="mt-3"
              onClick={() => {
                setPages(items);
                setCursor(q.data!.nextCursor);
              }}
            >
              {t('common.more')}
            </Button>
          )}
        </>
      )}
    </Section>
  );
}

function MemberTable({ items }: { items: SegmentMembers['items'] }) {
  const t = useT();
  const { formatDate } = useI18n();
  const money = useMoney();
  if (!items.length) return <p className="text-sm text-ink-3">{t('admin.segments.noMatches')}</p>;
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm" aria-label={t('admin.segments.colCustomer')}>
        <thead className="bg-surface-2 text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t('admin.segments.colCustomer')}</th>
            <th className="px-3 py-2 text-start font-medium">{t('admin.segments.colRfm')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.segments.colOrders')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.segments.colSpent')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.segments.colLastOrder')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-t border-line align-top">
              <td className="px-3 py-2">
                <Link
                  to="/musteriler/$id"
                  params={{ id: m.id }}
                  className="font-medium text-pine underline-offset-2 hover:underline"
                >
                  {m.name}
                </Link>
                <span className="block text-xs text-ink-3">{m.email}</span>
              </td>
              <td className="px-3 py-2">
                {m.rfmSegment ? (
                  <Badge tone="pine">{t(`admin.segments.groups.${m.rfmSegment}`)}</Badge>
                ) : (
                  <span className="text-ink-3">{t('admin.segments.groups.none')}</span>
                )}
              </td>
              <td className="px-3 py-2 text-end tabular-nums">{m.orderCount}</td>
              <td className="px-3 py-2 text-end tabular-nums">{money(m.totalSpentMinor, m.currency)}</td>
              <td className="px-3 py-2 text-end text-ink-2">{formatDate(m.lastOrderAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
